import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  Heading,
  Skeleton,
  Text,
  useMediaQuery,
  useToast,
} from '@chakra-ui/react';
import { AddIcon } from '@chakra-ui/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import CaregiversTable from './CaregiversTable.tsx';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext.tsx';
import { UserRole } from '../../constants/UserRoles.ts';
import { DEFAULT_CAREGIVER_PIN_LENGTH } from '../../constants/caregiverPin';
import { ICareHome } from '../../interfaces';
import { usePerPageStorage } from '../../hooks/usePerPageStorage';
import AleaFilterTags from '../../components/AleaFilterTags/AleaFilterTags.tsx';
import SearchBar from '../../components/SearchBar/SearchBar';
import AleaDownloadButton, {
  DownloadOption,
} from '../../components/AleaDownloadButton/AleaDownloadButton';
import { AleaSelectedTags } from '../../components/AleaSelectedTag';
import { debounce } from 'lodash';
import {
  A4ReportDocument,
  type ReportColumn,
  type ReportRow,
} from '../../pdf/reports/A4ReportDocument';
import {
  downloadReactPdf,
  formatExportTimestamp,
  printReactPdf,
} from '../../utils/reactPdfExport';
import { SeniSmartLogo } from '../../pdf/logos/SeniSmartLogo';
import { buildCsv, type CsvColumn, downloadCsv } from '../../utils/csvExport';
import { ICaregiver } from '../../services/CaregiverService';
import AddCaregiverModal from '../../modals/AddCaregiverModal/AddCaregiverModal';
import AleaConfirmModal from '../../components/AleaModal/AleaConfirmModal';
import AleaInfoModal from '../../modals/AleaInfoModal/AleaInfoModal';
import { usePageFiltersStorage } from '../../hooks/usePageFiltersStorage';
import {
  useCaregiversListQuery,
  useCareHomesForCaregiversQuery,
  useCareHomeForPinLengthQuery,
} from './caregiversPage.queries.ts';
import { useCaregiversPageMutations } from './caregiversPage.mutations.ts';

const EMPTY_CAREGIVERS: ICaregiver[] = [];

const CaregiversPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { isRestoring } = usePageFiltersStorage();
  const { user } = useAuth();
  const toast = useToast();
  const [isLargerThan1120] = useMediaQuery('(min-width: 1120px)');

  const { perPage: storedPerPage, updatePerPage } = usePerPageStorage();

  // Store current search input value that comes directly from SearchBar
  const inputValueRef = useRef<string>('');

  // Extract query from URL search params
  const queryParams = new URLSearchParams(location.search);
  const urlQueryParam = queryParams.get('query') || '';
  const urlPageParam = parseInt(queryParams.get('page') || '1', 10);
  const urlPerPageParam = queryParams.get('perPage');
  const showArchived = queryParams.get('show_archived') === 'true';

  // State for search query - initialize from URL
  const [searchQuery, setSearchQuery] = useState(urlQueryParam);

  // Table skeleton loader (e.g. immediate feedback while typing before debounce fires)
  const [isTableLoading, setIsTableLoading] = useState(false);

  // State for add caregiver modal
  const [isAddCaregiverModalOpen, setIsAddCaregiverModalOpen] = useState(false);
  const [editingCaregiver, setEditingCaregiver] = useState<ICaregiver | null>(
    null
  );
  const [caregiverToArchive, setCaregiverToArchive] =
    useState<ICaregiver | null>(null);
  const [activeActionRowId, setActiveActionRowId] = useState<string | null>(
    null
  );
  const canViewPin = user?.role !== UserRole.ORGANISATION_MANAGER;
  const [hiddenPinIds, setHiddenPinIds] = useState<Set<string>>(new Set());

  const togglePinVisibility = useCallback((id: string) => {
    setHiddenPinIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  // State for archiving info modal
  const [isArchivingInfoOpen, setIsArchivingInfoOpen] = useState(false);
  const [archivingCaregiverName, setArchivingCaregiverName] = useState<
    string | null
  >(null);

  // State for reactivating info modal
  const [isReactivatingInfoOpen, setIsReactivatingInfoOpen] = useState(false);
  const [reactivatingCaregiverName, setReactivatingCaregiverName] = useState<
    string | null
  >(null);

  // State for current page - initialize from URL
  const [currentPage, setCurrentPage] = useState(urlPageParam);

  // State for perPage - If URL has perPage, use it, otherwise use stored value
  const [perPage, setPerPage] = useState(
    urlPerPageParam ? parseInt(urlPerPageParam, 10) : storedPerPage
  );

  // State for filter tag (Active/Archived) - initialize from URL
  const [selectedFilter, setSelectedFilter] = useState(
    showArchived ? 'archived' : 'active'
  );

  const [isExporting, setIsExporting] = useState(false);
  const tableContainerRef = useRef<HTMLDivElement>(null);

  // CRUD actions are handled as React Query mutations (extracted to a separate file)
  const { updateCaregiverStatusMutation, invalidateCaregivers, isCrudLoading } =
    useCaregiversPageMutations();

  useEffect(() => {
    if (isRestoring) return;

    const params = new URLSearchParams(location.search);
    let shouldUpdate = false;

    // Keep URL canonical so React Query queryKey stays stable
    if (!params.get('page')) {
      params.set('page', '1');
      shouldUpdate = true;
    }
    if (!params.get('perPage')) {
      params.set('perPage', storedPerPage.toString());
      shouldUpdate = true;
    }

    if (shouldUpdate) {
      navigate(`${location.pathname}?${params.toString()}`, { replace: true });
    }
  }, [isRestoring, location.pathname, location.search, navigate, storedPerPage]);

  // Update states when URL changes
  useEffect(() => {
    const queryFromUrl = queryParams.get('query') || '';
    const pageFromUrl = parseInt(queryParams.get('page') || '1', 10);
    const perPageFromUrlRaw = queryParams.get('perPage');
    const perPageFromUrl = perPageFromUrlRaw
      ? parseInt(perPageFromUrlRaw, 10)
      : storedPerPage;
    const isArchived = queryParams.get('show_archived') === 'true';

    if (searchQuery !== queryFromUrl) {
      setSearchQuery(queryFromUrl);
    }

    if (currentPage !== pageFromUrl) {
      setCurrentPage(pageFromUrl);
    }

    if (perPage !== perPageFromUrl) {
      setPerPage(perPageFromUrl);
    }

    if (selectedFilter !== (isArchived ? 'archived' : 'active')) {
      setSelectedFilter(isArchived ? 'archived' : 'active');
    }
  }, [location.search, perPage, storedPerPage]);

  // Handle search input changes - this is called immediately when user types
  const handleSearch = (inputValue: string) => {
    inputValueRef.current = inputValue;

    // Show table skeleton loader immediately when user types (if value changed)
    if (inputValue !== searchQuery) {
      setIsTableLoading(true);
    }

    debouncedUpdateUrl();
  };

  // Debounced function to update URL with the latest input value
  const debouncedUpdateUrl = useCallback(
    debounce(() => {
      const latestInput = inputValueRef.current;
      const newSearchParams = new URLSearchParams(location.search);

      if (latestInput) {
        newSearchParams.set('query', latestInput);
      } else {
        newSearchParams.delete('query');
      }

      newSearchParams.set('page', '1');
      navigate(`${location.pathname}?${newSearchParams.toString()}`, {
        replace: true,
      });
    }, 1000),
    [location.pathname, location.search, navigate]
  );

  // Clear search handler
  const handleClearSearch = () => {
    inputValueRef.current = '';
    const newSearchParams = new URLSearchParams(location.search);
    newSearchParams.delete('query');
    newSearchParams.set('page', '1');
    navigate(`${location.pathname}?${newSearchParams.toString()}`, {
      replace: true,
    });
  };

  const careHomesQuery = useCareHomesForCaregiversQuery(
    user?.organisation?.id,
    user?.role === UserRole.ORGANISATION_MANAGER
  );

  const careHomes = useMemo((): ICareHome[] => {
    if (user?.role === UserRole.ORGANISATION_MANAGER) {
      return careHomesQuery.data?.items ?? [];
    }

    if (!user?.carehome) return [];

    // For non-organisation managers, include only their own care home
    return [
      {
        id: user.carehome.id,
        name: user.carehome.name,
        organisation_id: user?.organisation?.id || '',
        address: {} as any,
        manager: {} as any,
        timezone: '',
        features: [],
        is_active: true,
        created_at: '',
        updated_at: '',
        deleted_at: null,
        registration_id: '',
        day_start: null,
        day_end: null,
        night_start: null,
        night_end: null,
        carehome_phone_data: { code: 0, number: 0 },
      },
    ];
  }, [careHomesQuery.data?.items, user?.carehome, user?.organisation?.id, user?.role]);

  const pinLengthCareHomeQuery = useCareHomeForPinLengthQuery(
    user?.carehome?.id,
    user?.role !== UserRole.ORGANISATION_MANAGER
  );

  const pinLength = useMemo(() => {
    const len = pinLengthCareHomeQuery.data?.export_params?.pin_length;
    if (typeof len === 'number' && Number.isFinite(len) && len > 0) {
      return Math.floor(len);
    }
    return user?.carehome?.export_params?.pin_length || DEFAULT_CAREGIVER_PIN_LENGTH;
  }, [pinLengthCareHomeQuery.data?.export_params?.pin_length, user?.carehome?.export_params?.pin_length]);

  const isQueryReady = useMemo(() => {
    if (isRestoring) return false;
    if (!queryParams.has('perPage')) return false;
    if (!queryParams.has('page')) return false;
    return true;
  }, [isRestoring, location.search]);

  const caregiversQueryInput = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const page = parseInt(params.get('page') || '1', 10);
    const perPage = parseInt(params.get('perPage') || storedPerPage.toString(), 10);
    const query = params.get('query') || undefined;
    const isArchived = params.get('show_archived') === 'true';
    const orderBy = params.get('orderBy') || undefined;
    const sortOrderRaw = params.get('sortOrder');
    const sortOrder =
      sortOrderRaw === 'asc' || sortOrderRaw === 'desc'
        ? (sortOrderRaw as 'asc' | 'desc')
        : null;
    const tags = params.get('tags') || undefined;
    const carehomeId = params.get('carehome_id') || undefined;

    return {
      page,
      perPage,
      query,
      orderBy,
      sortOrder,
      tags,
      carehomeId,
      isActive: !isArchived,
    };
  }, [location.search, storedPerPage]);

  const caregiversQuery = useCaregiversListQuery(caregiversQueryInput, isQueryReady);

  useEffect(() => {
    if (!caregiversQuery.isFetching) {
      setIsTableLoading(false);
    }
    // Also close skeleton on cache hits (queryKey changes without fetching)
  }, [caregiversQuery.isFetching, caregiversQuery.dataUpdatedAt, caregiversQuery.errorUpdatedAt]);

  const caregivers = caregiversQuery.data?.items ?? EMPTY_CAREGIVERS;
  const totalCount = caregiversQuery.data?.total || 0;
  const isArchivedView = selectedFilter === 'archived';
  const canManageCaregivers = user?.role !== UserRole.ORGANISATION_MANAGER;
  const isSearching = !!queryParams.get('query');
  const isLoading =
    !isExporting &&
    (isCrudLoading ||
      caregiversQuery.isLoading ||
      (!isSearching && caregiversQuery.isFetching));

  useEffect(() => {
    setHiddenPinIds((prev) => {
      if (!canViewPin) {
        return prev.size === 0 ? prev : new Set<string>();
      }

      const next = new Set(caregivers.filter((c) => !!c.pin).map((c) => c.id));

      if (prev.size === next.size) {
        for (const id of prev) {
          if (!next.has(id)) return next;
        }
        return prev;
      }

      return next;
    });
  }, [canViewPin, caregivers]);

  // Make sure to clean up debounced function on unmount
  useEffect(() => {
    return () => {
      debouncedUpdateUrl.cancel();
    };
  }, [debouncedUpdateUrl]);

  const handlePageChange = (page: number) => {
    const newSearchParams = new URLSearchParams(location.search);
    newSearchParams.set('page', page.toString());
    navigate(`${location.pathname}?${newSearchParams.toString()}`, {
      replace: true,
    });
  };

  // Handler for perPage changes
  const handlePerPageChange = (newPerPage: number) => {
    updatePerPage(newPerPage);
    setPerPage(newPerPage);
    setCurrentPage(1);

    const newParams = new URLSearchParams(location.search);
    newParams.set('perPage', newPerPage.toString());
    newParams.set('page', '1');
    navigate(`${location.pathname}?${newParams.toString()}`);
  };

  // Filter tags for Active and Archived
  const filterTags = [
    { label: t('active'), value: 'active' },
    { label: t('general.archived'), value: 'archived' },
  ];

  // Handle filter tag selection
  const handleFilterSelect = (value: string) => {
    const newSearchParams = new URLSearchParams(location.search);

    if (value === 'archived') {
      newSearchParams.set('show_archived', 'true');
    } else {
      // Keep explicit value so filter restore does not keep stale 'true' from storage
      newSearchParams.set('show_archived', 'false');
    }

    newSearchParams.set('page', '1');
    navigate(`${location.pathname}?${newSearchParams.toString()}`, {
      replace: true,
    });
  };

  const buildPdfDocument = (exportDate: Date) => {
    const columns: ReportColumn[] = [
      { key: 'name', header: t('name'), flex: 2, align: 'left' },
      ...(user?.role === UserRole.ORGANISATION_MANAGER
        ? ([
            { key: 'carehome', header: t('careHome'), flex: 2, align: 'left' },
          ] as ReportColumn[])
        : []),
      { key: 'pin', header: t('pinCareDocSys'), flex: 1, align: 'left' },
    ];

    const rows: ReportRow[] = caregivers.map((c) => ({
      name: c.name || '-',
      ...(user?.role === UserRole.ORGANISATION_MANAGER
        ? { carehome: c.carehome?.name || '-' }
        : {}),
      pin: c.pin || '-',
    }));

    const carehomeIdForFilter =
      user?.role === UserRole.ORGANISATION_MANAGER
        ? queryParams.get('carehome_id') || undefined
        : user?.carehome?.id;

    const carehomeNameForFilter = carehomeIdForFilter
      ? careHomes.find((ch) => ch.id === carehomeIdForFilter)?.name || '-'
      : '-';

    return (
      <A4ReportDocument
        title={t('careGivers')}
        subtitle={t('totalItem', { count: totalCount })}
        headerRightLeadingLogo={{
          src: '/assets/images/logos/alea-cockpit-logo.png',
          height: 27,
        }}
        headerRightLabel={t('exclusively_with')}
        headerRightTrailing={<SeniSmartLogo width={73} height={24} />}
        filterLabel={isArchivedView ? t('general.archived') : t('active')}
        filterTagVariant={isArchivedView ? 'archived' : 'active'}
        filterValue={carehomeNameForFilter}
        columns={columns}
        rows={rows}
        organisationName={user?.organisation?.name}
        exportedAtLabel={t('general.export', { defaultValue: 'Export' })}
        exportedAtValue={formatExportTimestamp(exportDate)}
        pageLabel={t('general.page', { defaultValue: 'Page' })}
      />
    );
  };

  const handlePrint = async () => {
    if (isExporting) return;
    try {
      setIsExporting(true);
      const exportDate = new Date();
      await printReactPdf({
        title: `caregivers-${exportDate.toISOString().slice(0, 10)}`,
        reactPdfDocument: buildPdfDocument(exportDate),
      });
    } catch (e) {
      console.error('Failed to print PDF:', e);
    } finally {
      setIsExporting(false);
    }
  };

  // Handle export as PDF
  const handleExportPdf = async () => {
    if (isExporting) return;

    try {
      const exportDate = new Date();
      const exportDay = exportDate.toISOString().slice(0, 10);
      setIsExporting(true);

      await downloadReactPdf({
        filename: `caregivers-report-${exportDay}.pdf`,
        reactPdfDocument: buildPdfDocument(exportDate),
      });
    } catch (error) {
      console.error('Failed to export PDF:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportCsv = () => {
    const exportDate = new Date();
    const exportDay = exportDate.toISOString().slice(0, 10);

    const showCareHomeColumnForExport =
      user?.role === UserRole.ORGANISATION_MANAGER;

    type CsvRow = ICaregiver;
    const columns: CsvColumn<CsvRow>[] = [
      { header: t('name'), value: (r) => r.name || '-' },
      ...(showCareHomeColumnForExport
        ? ([
            { header: t('careHome'), value: (r) => r.carehome?.name || '-' },
          ] as CsvColumn<CsvRow>[])
        : []),
      { header: t('pinCareDocSys'), value: (r) => r.pin || '-' },
    ];

    const rows = caregivers;
    const csv = buildCsv(columns, rows);
    downloadCsv(`caregivers-report-${exportDay}.csv`, csv);
  };

  const downloadOptions: DownloadOption[] = [
    {
      id: 'print',
      label: 'Print',
      translationKey: 'general.print',
      onClick: handlePrint,
    },
    {
      id: 'pdf',
      label: 'Export as PDF',
      translationKey: 'general.exportAsPdf',
      onClick: handleExportPdf,
    },
    {
      id: 'csv',
      label: 'Export as CSV',
      translationKey: 'general.exportAsCsv',
      onClick: handleExportCsv,
    },
  ];

  const filters = {
    carehome_id:
      user?.role === UserRole.ORGANISATION_MANAGER
        ? careHomes.map((careHome) => ({
            label: careHome.name,
            value: careHome.name,
            id: careHome.id,
          }))
        : [],
  };


  const handleEditCaregiver = (caregiver: ICaregiver) => {
    setEditingCaregiver(caregiver);
    setIsAddCaregiverModalOpen(true);
  };

  const handleRequestArchiveCaregiver = (caregiver: ICaregiver) => {
    setActiveActionRowId(null);
    setCaregiverToArchive(caregiver);
  };

  const handleArchiveCaregiver = async (caregiver: ICaregiver) => {
    try {
      await updateCaregiverStatusMutation.mutateAsync({
        caregiver,
        isActive: false,
      });
    } catch (error) {
      console.error('Failed to archive caregiver', error);
      toast({
        title: t('error'),
        description: t('caregiver.archiveFailed', {
          defaultValue: 'Failed to archive caregiver',
        }),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleReactivateCaregiver = async (caregiver: ICaregiver) => {
    try {
      setReactivatingCaregiverName(caregiver.name);
      setIsReactivatingInfoOpen(true);
      await updateCaregiverStatusMutation.mutateAsync({
        caregiver,
        isActive: true,
      });
    } catch (error) {
      console.error('Failed to reactivate caregiver', error);
      toast({
        title: t('error'),
        description: t('caregiver.reactivateFailed', {
          defaultValue: 'Failed to reactivate caregiver',
        }),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsReactivatingInfoOpen(false);
      setReactivatingCaregiverName(null);
    }
  };

  return (
    <Box data-testid="caregivers-page">
      <Flex
        justifyContent="space-between"
        alignItems={{ base: 'flex-start', md: 'center' }}
        mb={{ base: '20px', md: '10px' }}
        h={{ base: 'auto', md: 75 }}
        flexDirection="row"
      >
        <Flex flexDirection="column">
          <Heading
            size="lg"
            fontSize={25}
            fontWeight={500}
            pl={{ base: 0, md: 0 }}
            mb={{ base: 1, md: 0 }}
          >
            {t('careGivers')}
          </Heading>
          {isLoading ? (
            <Skeleton
              height="24px"
              width="200px"
              mt="4px"
              startColor="#c0c0c0"
              endColor="#e0e0e0"
            />
          ) : (
            <Text fontSize={15} fontWeight={300} color="alea.500" mt="4px">
              {t('totalItem', { count: totalCount })}
            </Text>
          )}
        </Flex>
        {canManageCaregivers && (
          <Button
            data-testid="caregivers-add-button"
            leftIcon={<AddIcon />}
            colorScheme="teal"
            variant="solid"
            h="50px"
            borderRadius="20px"
            fontWeight={400}
            fontSize="20px"
            whiteSpace="nowrap"
            onClick={() => {
              setEditingCaregiver(null);
              setIsAddCaregiverModalOpen(true);
            }}
          >
            {t('addCaregiver')}
          </Button>
        )}
      </Flex>

      <Box mb="25px">
        <Flex
          justifyContent="space-between"
          alignItems="flex-start"
          flexDirection={{
            base: 'column',
            md: isLargerThan1120 ? 'row' : 'column',
          }}
          gap={{ base: '16px' }}
          flexWrap={'wrap'}
        >
          <AleaFilterTags
            tags={filterTags}
            selectedTags={[selectedFilter]}
            onTagSelect={handleFilterSelect}
          />
          <Flex
            gap="10px"
            alignItems="center"
            position="relative"
            alignSelf="flex-start"
            width={{ base: '100%', md: 'auto' }}
          >
            <Box w={{ base: '100%', md: '320px' }}>
              <SearchBar
                onClose={handleClearSearch}
                onSearch={handleSearch}
                searchQuery={searchQuery}
                isHardwarePage={true}
                height="50px"
                placeholderLanguageKey="name"
              />
            </Box>
            {user?.role === UserRole.ORGANISATION_MANAGER && <></>}
            <AleaDownloadButton options={downloadOptions} />
          </Flex>
        </Flex>
      </Box>

      <AleaSelectedTags filters={filters} />

      <Box
        bg="white"
        borderRadius="20px"
        overflow="auto"
        minHeight={500}
        ref={tableContainerRef}
        className="caregivers-table-container"
      >
        <CaregiversTable
          caregivers={caregivers}
          isLoading={isLoading || isTableLoading}
          total={totalCount}
          showCareHomeColumn={user?.role === UserRole.ORGANISATION_MANAGER}
          canViewPin={canViewPin}
          hiddenPinIds={hiddenPinIds}
          togglePinVisibility={togglePinVisibility}
          usePagination={true}
          currentPage={currentPage}
          perPage={perPage}
          onPageChange={handlePageChange}
          onPerPageChange={handlePerPageChange}
          onEditCaregiver={handleEditCaregiver}
          onArchiveCaregiver={handleRequestArchiveCaregiver}
          onReactivateCaregiver={handleReactivateCaregiver}
          isArchivedView={isArchivedView}
          canManageCaregivers={canManageCaregivers}
          activeActionRowId={activeActionRowId}
          setActiveActionRowId={setActiveActionRowId}
        />
      </Box>

      <AddCaregiverModal
        isOpen={isAddCaregiverModalOpen}
        onClose={() => {
          setIsAddCaregiverModalOpen(false);
          setEditingCaregiver(null);
        }}
        carehomeId={user?.carehome?.id}
        pinLength={pinLength}
        caregiver={editingCaregiver}
        onConfirm={() => {
          invalidateCaregivers();
        }}
      />

      <AleaConfirmModal
        isOpen={!!caregiverToArchive}
        onClose={() => setCaregiverToArchive(null)}
        title={
          caregiverToArchive
            ? t('archiveCaregiverConfirmationHeadline', {
                caregiverName: caregiverToArchive.name,
              })
            : t('archive')
        }
        description={t('archiveCaregiverConfirmationSubline')}
        onConfirm={async () => {
          if (!caregiverToArchive) return;
          const name = caregiverToArchive.name;
          const caregiver = caregiverToArchive;
          setCaregiverToArchive(null);
          setArchivingCaregiverName(name);
          setIsArchivingInfoOpen(true);
          await handleArchiveCaregiver(caregiver);
          setIsArchivingInfoOpen(false);
          setArchivingCaregiverName(null);
        }}
      />

      {/* Archiving Caregiver Info Modal */}
      <AleaInfoModal
        isOpen={isArchivingInfoOpen}
        onClose={() => {
          setIsArchivingInfoOpen(false);
          setArchivingCaregiverName(null);
        }}
        message={
          archivingCaregiverName
            ? t('archivingCaregiver', { caregiverName: archivingCaregiverName })
            : t('archivingCaregiver', { caregiverName: '' })
        }
      />

      {/* Reactivating Caregiver Info Modal */}
      <AleaInfoModal
        isOpen={isReactivatingInfoOpen}
        onClose={() => {
          setIsReactivatingInfoOpen(false);
          setReactivatingCaregiverName(null);
        }}
        message={
          reactivatingCaregiverName
            ? t('reactivatingCaregiver', { caregiverName: reactivatingCaregiverName })
            : t('reactivatingCaregiver', { caregiverName: '' })
        }
      />

      {/* Backdrop when caregiver row action menu is open */}
      {activeActionRowId && (
        <Box
          position="fixed"
          top={0}
          left={0}
          w="100vw"
          h="100vh"
          bg="rgba(0, 0, 0, 0.20)"
          zIndex={1500}
          onClick={() => setActiveActionRowId(null)}
        />
      )}
    </Box>
  );
};

export default CaregiversPage;
